import React, { useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  CheckCircle2,
  CircleAlert,
  CreditCard,
  Package,
  Settings2,
  Sparkles,
  UserRound,
  X
} from 'lucide-react';
import { Modal } from '../../components/ui/Modal';
import { useAppSelector } from '../../hooks/useAppRedux';
import { useI18n } from '../../hooks/useI18n';
import { classNames } from '../../utils/classNames';
import { APP_VERSION } from './personalizationConstants';
import { GeneralSettingsPanel } from './GeneralSettingsPanel';
import { PackageSettingsPanel } from './PackageSettingsPanel';
import { PersonalizationPanel } from './PersonalizationPanel';
import { settingsControlClass } from './settingsUi';
import { useSettingsModal } from './SettingsModalContext';
import { UserProfileForm } from './UserProfileForm';
import { WalletSection } from './WalletSection';

const getInitials = (name = '') =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase() || '?';

const PaymentBanner = ({ variant, message, onDismiss, dismissLabel }) => {
  const success = variant === 'success';

  return (
    <div
      role="alert"
      className={classNames(
        'mb-4 flex items-start gap-3 rounded-2xl border px-3.5 py-3 text-sm',
        success
          ? 'border-emerald-200/80 bg-emerald-50 text-emerald-800 dark:border-emerald-500/25 dark:bg-emerald-500/10 dark:text-emerald-100'
          : 'border-rose-200/80 bg-rose-50 text-rose-800 dark:border-rose-500/25 dark:bg-rose-500/10 dark:text-rose-100'
      )}
    >
      <span
        className={classNames(
          'mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl',
          success ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20' : 'bg-rose-100 text-rose-700 dark:bg-rose-500/20'
        )}
      >
        {success ? <CheckCircle2 size={16} aria-hidden /> : <CircleAlert size={16} aria-hidden />}
      </span>
      <p className="min-w-0 flex-1 leading-6">{message}</p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={dismissLabel}
        className={classNames(settingsControlClass, 'inline-flex h-8 w-8 items-center justify-center text-current')}
      >
        <X size={14} />
      </button>
    </div>
  );
};

export const SettingsModal = () => {
  const { t, language, formatNumber } = useI18n();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const user = useAppSelector((state) => state.auth.user);
  const { open, tab, setTab, closeSettings, paymentAlert, setPaymentAlert } = useSettingsModal();
  const isRtl = language === 'fa';

  const navGroups = useMemo(
    () => [
      {
        id: 'preferences',
        label: t('settingsSectionPreferences'),
        items: [
          {
            id: 'general',
            label: t('settingsGeneralTab'),
            hint: t('settingsGeneralHint'),
            icon: Settings2
          },
          {
            id: 'personalization',
            label: t('settingsPersonalizationTab'),
            hint: t('settingsPersonalizationHint'),
            icon: Sparkles
          }
        ]
      },
      {
        id: 'account',
        label: t('settingsSectionAccount'),
        items: [
          {
            id: 'profile',
            label: t('settingsUserTab'),
            hint: t('settingsProfileHint'),
            icon: UserRound
          },
          {
            id: 'packages',
            label: t('settingsPackagesTab'),
            hint: t('settingsPackagesHint'),
            icon: Package
          },
          {
            id: 'wallet',
            label: t('settingsWalletTab'),
            hint: t('settingsWalletHint'),
            icon: CreditCard
          }
        ]
      }
    ],
    [t]
  );

  const navItems = useMemo(() => navGroups.flatMap((group) => group.items), [navGroups]);
  const activeItem = navItems.find((item) => item.id === tab) || navItems[0];
  const displayName = user?.fullName || user?.name || t('settingsGuestName');
  const mobileLabel = user?.mobile ? formatNumber(user.mobile) : '';

  const moveTab = (delta) => {
    const index = navItems.findIndex((item) => item.id === tab);
    const nextIndex = (index + delta + navItems.length) % navItems.length;
    setTab(navItems[nextIndex].id);
  };

  const handleNavKeyDown = (event) => {
    if (event.key === 'Home') {
      event.preventDefault();
      setTab(navItems[0].id);
      return;
    }

    if (event.key === 'End') {
      event.preventDefault();
      setTab(navItems[navItems.length - 1].id);
      return;
    }

    const nextKey = isRtl ? 'ArrowLeft' : 'ArrowRight';
    const prevKey = isRtl ? 'ArrowRight' : 'ArrowLeft';

    if (event.key === 'ArrowDown' || event.key === nextKey) {
      event.preventDefault();
      moveTab(1);
    }

    if (event.key === 'ArrowUp' || event.key === prevKey) {
      event.preventDefault();
      moveTab(-1);
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <Modal
      open={open}
      onClose={closeSettings}
      title={t('settingsModalTitle')}
      hideHeader
      overlayClassName="z-[55] bg-slate-950/60 p-3 sm:p-5"
      panelClassName="flex h-[min(42rem,90vh)] !max-w-5xl flex-col overflow-hidden !rounded-[1.75rem] !p-0"
    >
      <div className="flex min-h-0 flex-1 flex-col md:flex-row">
        <aside className="relative flex shrink-0 flex-col border-b border-border/80 bg-white/45 md:w-[17.5rem] md:border-b-0 md:border-e dark:bg-slate-950/25">
          <div
            className="pointer-events-none absolute -top-16 inset-inline-start-6 h-36 w-36 rounded-full bg-accent/15 blur-3xl motion-reduce:hidden"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute bottom-8 inset-inline-end-0 h-24 w-24 rounded-full bg-teal-400/10 blur-3xl motion-reduce:hidden"
            aria-hidden
          />

          <div className="relative flex items-center justify-between gap-3 px-4 pb-3 pt-4 md:px-5">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted">{t('settings')}</p>
              <p className="mt-0.5 text-lg font-semibold tracking-tight text-text">{t('settingsModalTitle')}</p>
            </div>
            <button
              type="button"
              onClick={closeSettings}
              aria-label={t('close')}
              className={classNames(
                settingsControlClass,
                'inline-flex h-10 w-10 items-center justify-center rounded-xl border border-border bg-white/80 text-muted hover:text-text md:hidden dark:bg-slate-950/50'
              )}
            >
              <X size={18} />
            </button>
          </div>

          <div className="relative mx-4 mb-3 flex items-center gap-3 rounded-2xl border border-border/80 bg-white/70 px-3 py-2.5 md:mx-5 dark:bg-slate-950/40">
            {user?.avatarUrl ? (
              <img src={user.avatarUrl} alt="" className="h-10 w-10 rounded-2xl object-cover" />
            ) : (
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-accent-soft text-sm font-bold text-accent">
                {getInitials(displayName)}
              </span>
            )}
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-text">{displayName}</p>
              {mobileLabel ? <p className="truncate text-xs text-muted">{mobileLabel}</p> : null}
            </div>
          </div>

          <nav
            className="relative flex gap-1 overflow-x-auto px-3 pb-3 md:flex-1 md:flex-col md:gap-4 md:overflow-y-auto md:px-3 md:pb-4"
            aria-label={t('settingsModalTitle')}
            onKeyDown={handleNavKeyDown}
          >
            {navGroups.map((group) => (
              <div key={group.id} className="flex gap-1 md:flex-col md:gap-1">
                <p className="hidden px-3 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-muted md:block">
                  {group.label}
                </p>
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const active = tab === item.id;

                  return (
                    <button
                      key={item.id}
                      type="button"
                      role="tab"
                      id={`settings-tab-${item.id}`}
                      aria-controls={`settings-panel-${item.id}`}
                      aria-selected={active}
                      onClick={() => setTab(item.id)}
                      className={classNames(
                        settingsControlClass,
                        'inline-flex shrink-0 items-center gap-2.5 rounded-2xl px-2.5 py-2 text-start',
                        active
                          ? 'bg-white text-text shadow-[0_1px_2px_rgba(15,23,42,0.08)] ring-1 ring-border dark:bg-accent-soft dark:shadow-none dark:ring-transparent'
                          : 'text-muted hover:bg-white/70 hover:text-text dark:hover:bg-white/5'
                      )}
                    >
                      <span
                        className={classNames(
                          'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl',
                          active ? 'bg-accent text-white dark:text-slate-950' : 'bg-white/80 text-muted dark:bg-slate-950/50'
                        )}
                      >
                        <Icon size={15} aria-hidden />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-medium">{item.label}</span>
                        <span className="mt-0.5 hidden text-[11px] leading-4 text-muted md:line-clamp-2">{item.hint}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            ))}
          </nav>

          <p className="relative mt-auto hidden px-5 pb-4 text-[11px] text-muted/70 md:block">v{APP_VERSION}</p>
        </aside>

        <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-panel/40">
          <header className="flex items-start justify-between gap-3 border-b border-border/70 px-4 py-4 sm:px-6">
            <div className="min-w-0">
              <h3 className="text-base font-semibold tracking-tight text-text">{activeItem.label}</h3>
              <p className="mt-1 text-xs leading-5 text-muted">{activeItem.hint}</p>
            </div>
            <button
              type="button"
              onClick={closeSettings}
              aria-label={t('close')}
              className={classNames(
                settingsControlClass,
                'hidden h-10 w-10 items-center justify-center rounded-xl border border-border bg-white/80 text-muted hover:text-text md:inline-flex dark:bg-slate-950/40'
              )}
            >
              <X size={18} />
            </button>
          </header>

          <div
            id={`settings-panel-${activeItem.id}`}
            role="tabpanel"
            aria-labelledby={`settings-tab-${activeItem.id}`}
            className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-6 sm:py-5"
          >
            {paymentAlert ? (
              <PaymentBanner
                variant={paymentAlert === 'success' ? 'success' : 'error'}
                message={paymentAlert === 'success' ? t('settingsPaymentSuccess') : t('settingsPaymentFailed')}
                onDismiss={() => setPaymentAlert(null)}
                dismissLabel={t('close')}
              />
            ) : null}

            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={tab}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
                className="motion-reduce:transform-none"
              >
                {tab === 'general' ? <GeneralSettingsPanel /> : null}
                {tab === 'personalization' ? <PersonalizationPanel /> : null}
                {tab === 'profile' ? <UserProfileForm /> : null}
                {tab === 'packages' ? <PackageSettingsPanel /> : null}
                {tab === 'wallet' ? <WalletSection /> : null}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </Modal>
  );
};
